import {api, RequestParam} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {DownloadStatus} from "@/shared/ui/downloadStatus";
import {renderOvertimesPopoverContent, renderTravellingTimesPopoverContent} from "./timeAccountPopoverUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {SecurityStatus} from "@/shared/securityStatus";
import {
	formatDate,
	formatDateTime,
	formatDuration,
	formatVacation,
	getCurrentISOOffset,
	parseInstant,
	parseISODate,
	Temporal
} from "@/shared/utils/datefunctions";
import {TimeAccountTableRow} from "@/shared/timr";
import {ExportType, TimeAccountEntryType, VacationUnit} from "@/shared/timrTypes";

export interface TimeAccountTableConfig extends BasicDataTableConfig {
	timeAccountBalanceChanged?: () => void;
	editTimeAccountInitialization?: (timeAccount: TimeAccountTableRow) => void;
	editVacationBalanceCorrection?: (timeAccount: TimeAccountTableRow) => void;
	editWorkBalanceCorrection?: (timeAccount: TimeAccountTableRow) => void;
	showContextMenuDeleteButton: boolean;
	showUserNameColumn: boolean;
	showOvertimesColumn: boolean;
	showTravellingTimesColumn: boolean;
}

export class TimeAccountTable extends BasicDataTable<TimeAccountTableRow> {
	public static EVENT_DELETED_TIME_ACCOUNT = "event_deletedTimeAccount";

	public static buildFilterParams(groupId: number | null | undefined, userId: number | null | undefined, timeAccountEntryType: TimeAccountEntryType | null): RequestParam[] {
		return [
			{name: "groupId", value: groupId},
			{name: "userId", value: userId},
			{name: "timeAccountFilterAction", value: timeAccountEntryType},
		];
	}

	constructor(public config: TimeAccountTableConfig) {
		super("/timr/timeaccounts/getTimeAccounts.json", config);
		this.initPopover();

		$(this).on(BasicDataTable.EVENT_DBLCLICK, (e, timeAccount: TimeAccountTableRow) => this.editTimeAccount(timeAccount));
	}

	private initPopover() {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;
		// Only one popover per element, so define it on the parent
		this.config.element.parent().popover({
			trigger: "hover",
			placement: "top",
			selector: `tbody > tr > td`,
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			html: true,
			template: "<div class=\"popover time-account-popover\" role=\"tooltip\"><div class=\"arrow\"></div><div class=\"popover-content\"></div></div>",
			content: function (this: HTMLElement) {
				const hoveredElement = $(this);
				const timeAccount = that.getRowData(hoveredElement);
				if (!timeAccount) {
					return null;
				}

				// Show either overtime or time account popover based on the hovered element
				if (hoveredElement.is(".overtime-accounts-popover-anchor")) {
					// it's not necessary to check SecurityStatus.ifOvertimeAllowanceAllowed(), because the overtime column (.overtime-accounts-anchor) is invisible if this is not the case
					return renderOvertimesPopoverContent(timeAccount.overtimes, timeAccount.allowancesOnly, timeAccount.timeAccountEntryType);
				} else if (hoveredElement.is(".time-account-popover-anchor")) {
					return that.renderPopoverContent(timeAccount);
				} else if (hoveredElement.is(".time-account-created-popover-anchor")) {
					return that.renderCreatedPopoverContent(timeAccount);
				} else if (hoveredElement.is(".travelling-time-accounts-popover-anchor")) {
					// it's not necessary to check SecurityStatus.ifTravellingTimesAllowed(), because the overtime column (.travelling-time-accounts-popover-anchor) is invisible if this is not the case
					return renderTravellingTimesPopoverContent(timeAccount.travellingTimes, timeAccount.timeAccountEntryType);
				}
			}
		});
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
		};
	}

	public filterByGroupAndUserAndAction(groupId: number | null | undefined, userId: number | null | undefined, timeAccountEntryType: TimeAccountEntryType) {
		return this.filter(TimeAccountTable.buildFilterParams(groupId, userId, timeAccountEntryType));
	}

	protected getColumnSettings(): TypedColumnSettings<TimeAccountTableRow>[] {
		return [
			{data: "id", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "date", orderable: true, render: BasicDataTable.renderDate, className: "time-account-created-popover-anchor"},
			{data: "userFullname", visible: this.config.showUserNameColumn, orderable: false, render: BasicDataTable.renderText},
			{
				data: "workDurationTarget",
				className: "border-left border-left-grey-300 slim-numbers-column time-account-popover-anchor",
				orderable: false,
				render: (d, t, r, m) => this.renderWorkDurationTarget(d, t, r, m),
				createdCell: (td, c, r: TimeAccountTableRow) => !r.workingTimeTracking || r.trustBasedWorkingTime ? $(td).attr("colspan", "4").addClass("text-center border-right border-right-grey-300") : null
			},
			{
				data: "workDurationTotal",
				className: "slim-numbers-column time-account-popover-anchor",
				orderable: false,
				render: (d, t, r, m) => this.renderWorkDurationTotal(d, t, r, m),
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.workingTimeTracking || r.trustBasedWorkingTime) {
						$(td).remove();
					}
				}
			},
			{
				data: "workDurationBalance",
				className: "slim-numbers-column border-left border-left-grey-300 time-account-popover-anchor",
				orderable: false,
				render: (d, t, r, m) => this.renderWorkDurationBalance(d, t, r, m),
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.workingTimeTracking || r.trustBasedWorkingTime) {
						$(td).remove();
					}
				}
			},
			{
				data: "workDurationTotalBalance",
				orderable: false,
				className: "text-right border-right border-right-grey-300 time-account-popover-anchor slim-numbers-column",
				render: BasicDataTable.renderHours,
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.workingTimeTracking || r.trustBasedWorkingTime) {
						$(td).remove();
					}
				}
			},
			{
				data: "overtimeAllowanceBalanceSum",
				orderable: false,
				className: "slim-numbers-column-center overtime-accounts-popover-anchor",
				visible: this.config.showOvertimesColumn,
				render: BasicDataTable.renderHours
			},
			{
				data: "overtimeSum",
				orderable: false,
				className: "slim-numbers-column-center border-right border-right-grey-300 overtime-accounts-popover-anchor",
				visible: this.config.showOvertimesColumn,
				render: BasicDataTable.renderHours
			},
			{
				data: "travellingTimeBalanceSum",
				orderable: false,
				className: "slim-numbers-column-center travelling-time-accounts-popover-anchor",
				visible: this.config.showTravellingTimesColumn,
				render: BasicDataTable.renderHours
			},
			{
				data: "travellingTimeSum",
				orderable: false,
				className: "slim-numbers-column-center border-right border-right-grey-300 travelling-time-accounts-popover-anchor",
				visible: this.config.showTravellingTimesColumn,
				render: BasicDataTable.renderHours
			},
			{
				data: "vacationNew",
				orderable: false,
				className: "slim-numbers-column time-account-popover-anchor",
				render: (d, t, r, m) => this.renderVacationNew(d, t, r, m),
				createdCell: (td, c, r: TimeAccountTableRow) => !r.vacationAccount ? $(td).attr("colspan", "4").addClass("text-center border-right border-right-grey-300") : null
			},
			{
				data: "vacationConsumed",
				orderable: false,
				className: "slim-numbers-column time-account-popover-anchor",
				render: this.renderVacation,
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "vacationTotalBalance",
				orderable: false,
				className: "slim-numbers-column time-account-popover-anchor",
				render: this.renderVacation,
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{
				data: "vacationUnit",
				orderable: false,
				className: "border-right border-right-grey-300 time-account-popover-anchor",
				render: this.renderVacationUnit,
				createdCell: (td, c, r: TimeAccountTableRow) => {
					if (!r.vacationAccount) {
						$(td).remove();
					}
				}
			},
			{data: "timeAccountEntryType", orderable: false, render: this.renderTimeAccountEntryType},
			{data: "description", orderable: false, className: "table-data-description", render: BasicDataTable.renderText}
		];
	}

	private renderWorkDurationTarget(data: number, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		if (row.workingTimeTracking && !row.trustBasedWorkingTime) {
			return BasicDataTable.renderHours(data, type, row, meta);
		} else {
			const message = !row.workingTimeTracking ? messages["reports.export.timeAccount.workingTimeTracking"] : messages["reports.export.timeAccount.trustBasedWorkingTime"];
			return `<div class="text-grey text-center"><i class="fal fa-info-circle position-left"></i>${message}</div>`;
		}
	}

	private renderWorkDurationTotal(data: number, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		if (data === 0 && row.timeAccountEntryType !== TimeAccountEntryType.Balancing) {
			return BasicDataTable.renderEmpty(data, type, row, meta);
		} else {
			return BasicDataTable.renderHours(data, type, row, meta);
		}
	}

	private renderWorkDurationBalance(data: number, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		if (row.timeAccountEntryType === TimeAccountEntryType.Payoff || row.timeAccountEntryType === TimeAccountEntryType.ExitPayoff) {
			return BasicDataTable.renderHours(row.workBalancePayoffs, type, row, meta);
		} else {
			return BasicDataTable.renderHours(data, type, row, meta);
		}
	}

	protected getDefaultOrder(): (string | number)[][] {
		// DEVNOTE: The backend sorts on multiple levels, even if not defined here. So if "date" is equal, the backend sorts by "id". This is done in TimeAccountController
		// 			because multi-level sorting via the DataTables definition getDefaultOrder() is not yet implemented in the backend
		return [[1, "desc"]];
	}

	protected renderTimeAccountEntryType(this: void, data: TimeAccountEntryType, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		return messages["timeAccount.timeAccountEntryType." + data];
	}

	private onTimeAccountBalanceChanged() {
		if (this.config.timeAccountBalanceChanged) {
			this.config.timeAccountBalanceChanged();
		}
	}

	private deleteTimeAccount(timeAccount: TimeAccountTableRow) {
		api("timeaccounts/deleteTimeAccount.json")
			.post({
				id: timeAccount.id
			})
			.done(() => {
				this.reload();
				this.onTimeAccountBalanceChanged();
				$(this).trigger(TimeAccountTable.EVENT_DELETED_TIME_ACCOUNT, timeAccount.timeAccountEntryType);
			});
	}

	private downloadTimeSheet(timeAccount: TimeAccountTableRow) {
		return api("timeaccounts/downloadTimeSheet.json")
			.get({
				ids: timeAccount.id,
				timeZone: getCurrentISOOffset()
			})
			.done(data => {
				if (data.exportJobId) {
					new DownloadStatus({
						exportJobId: data.exportJobId,
						exportType: ExportType.Pdf,
						allowCancellation: true,
						loadingMessage: messages["downloadStatus.loadingMessage.timeAccount"]
					});
				}
			});
	}

	private confirmDeleteTimeAccount(timeAccount: TimeAccountTableRow) {
		// simplify and clarify the checks by extracting the entry type and grouping related types
		const entryType = timeAccount.timeAccountEntryType;
		const overtimeTypes = [
			TimeAccountEntryType.OvertimeCorrection,
			TimeAccountEntryType.OverTimePayoff,
			TimeAccountEntryType.OvertimeTimeAccountTransfer
		];
		const travellingTypes = [
			TimeAccountEntryType.TravellingTimeCorrection,
			TimeAccountEntryType.TravellingTimePayoff,
			TimeAccountEntryType.TravellingTimeTransfer
		];

		if (overtimeTypes.includes(entryType) && !SecurityStatus.isOvertimeAllowanceAllowed()) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.delete.notAllowed.overtime"
			});
		} else if (travellingTypes.includes(entryType) && !SecurityStatus.isTravellingTimeAllowed()) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.delete.notAllowed.travellingTime"
			});
		} else if (timeAccount.deletable) {
			BootstrapModal.confirm({
				title: "dialog.warning.title",
				message: "timeAccounts.context.delete.confirmation." + entryType,
				escapeText: false,
				yesLabel: "timeAccounts.context.delete",
				yesAction: modal => {
					this.deleteTimeAccount(timeAccount);
					modal.close();
				},
				noLabel: "confirmation.cancel"
			});
			return;
		} else {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.delete.notAllowed"
			});
		}
	}

	private renderVacation(this: void, data: number | null, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		return data != null ? formatVacation(data, row.vacationUnit) : "";
	}

	private renderVacationNew(this: void, data: number | null, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		if (row.vacationAccount) {
			return data != null ? formatVacation(data, row.vacationUnit) : "";
		} else {
			return `<div class="text-grey text-center"><i class="fal fa-info-circle position-left"></i>${messages["reports.export.timeAccount.vacationAccount"]}</div>`;
		}
	}

	private renderVacationUnit(this: void, data: VacationUnit, type: string, row: TimeAccountTableRow, meta: DataTables.CellMetaSettings): string {
		if (row.vacationNew != null || row.vacationConsumed != null || row.vacationTotalBalance != null) {
			return messages["vacationUnit." + data];
		} else {
			return "";
		}
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "timeAccounts.context.edit",
				action: timeAccount => this.editTimeAccount(timeAccount),
				show: timeAccount => this.config.showContextMenuDeleteButton && this.isTimeAccountEditable(timeAccount),
			})
			.addMenuEntry({
				label: "timeAccounts.context.delete",
				action: timeAccount => this.confirmDeleteTimeAccount(timeAccount),
				show: this.config.showContextMenuDeleteButton
			})
			.addMenuEntry({
				label: "timeAccounts.context.download",
				action: timeAccount => this.downloadTimeSheet(timeAccount),
				show: timeAccount => timeAccount.workingTimeTracking && !timeAccount.trustBasedWorkingTime &&
					timeAccount.timeAccountEntryType === TimeAccountEntryType.Balancing || timeAccount.timeAccountEntryType === TimeAccountEntryType.Exit
			});
	}

	private editTimeAccount(timeAccount: TimeAccountTableRow) {
		if (this.isInitializationType(timeAccount)) {
			this.editTimeAccountInitialization(timeAccount);
		} else if (this.isVacationBalanceCorrectionType(timeAccount)) {
			this.editVacationBalanceCorrection(timeAccount);
		} else if (this.isWorkBalanceCorrectionType(timeAccount)) {
			this.editWorkBalanceCorrection(timeAccount);
		}
	}

	private isTimeAccountEditable(timeAccount: TimeAccountTableRow): boolean {
		return this.isInitializationType(timeAccount)
			|| this.isVacationBalanceCorrectionType(timeAccount)
			|| this.isWorkBalanceCorrectionType(timeAccount);
	}

	private editTimeAccountInitialization(timeAccount: TimeAccountTableRow) {
		if (timeAccount.timeAccountEntryType === TimeAccountEntryType.InitializationLegacy) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed.legacy"
			});
		} else if (timeAccount.deletable) {
			this.config.editTimeAccountInitialization?.(timeAccount);
		} else {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed"
			});
		}
	}

	private editVacationBalanceCorrection(timeAccount: TimeAccountTableRow) {
		if (timeAccount.deletable) {
			this.config.editVacationBalanceCorrection?.(timeAccount);
		} else {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed"
			});
		}

	}

	private isInitializationType(timeAccount: TimeAccountTableRow) {
		return [
			TimeAccountEntryType.Initialization,
			TimeAccountEntryType.InitializationLegacy,
			TimeAccountEntryType.UserEntry,
			TimeAccountEntryType.ReEntry
		].includes(timeAccount.timeAccountEntryType);
	}

	private isVacationBalanceCorrectionType(timeAccount: TimeAccountTableRow) {
		return timeAccount.timeAccountEntryType === TimeAccountEntryType.VacationBalanceCorrection;
	}

	private isWorkBalanceCorrectionType(timeAccount: TimeAccountTableRow): boolean {
		const correctionTypes = [
			TimeAccountEntryType.WorkBalanceCorrection, TimeAccountEntryType.Payoff,
			TimeAccountEntryType.OvertimeCorrection, TimeAccountEntryType.OverTimePayoff,
			TimeAccountEntryType.TravellingTimeCorrection, TimeAccountEntryType.TravellingTimePayoff,
			TimeAccountEntryType.OvertimeTimeAccountTransfer, TimeAccountEntryType.TravellingTimeTransfer
		];
		return correctionTypes.includes(timeAccount.timeAccountEntryType);
	}

	private editWorkBalanceCorrection(timeAccount: TimeAccountTableRow) {
		const overtimeTypes = [
			TimeAccountEntryType.OvertimeCorrection,
			TimeAccountEntryType.OverTimePayoff,
			TimeAccountEntryType.OvertimeTimeAccountTransfer
		];
		const travellingTypes = [
			TimeAccountEntryType.TravellingTimeCorrection,
			TimeAccountEntryType.TravellingTimePayoff,
			TimeAccountEntryType.TravellingTimeTransfer
		];

		const isOvertimeType = overtimeTypes.includes(timeAccount.timeAccountEntryType);
		const isTravellingType = travellingTypes.includes(timeAccount.timeAccountEntryType);

		if (isOvertimeType && !SecurityStatus.isOvertimeAllowanceAllowed()) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed.overtime"
			});
		} else if (isTravellingType && !SecurityStatus.isTravellingTimeAllowed()) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed.travellingTime"
			});
		} else if (timeAccount.deletable) {
			this.config.editWorkBalanceCorrection?.(timeAccount);
		} else {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "timeAccounts.context.edit.notAllowed"
			});
		}
	}

	private buildPopoverTimeAccountTable(timeAccount: TimeAccountTableRow): JQuery {
		const table = $("<table>").addClass("time-account-table");
		table.append($("<tr>")
			.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages.withArguments("timeAccount.tooltip.period",
				formatDate(parseISODate(timeAccount.dateFrom)),
				formatDate(parseISODate(timeAccount.date)))))
			.append($("<th>").attr("rowspan", 8).addClass("time-account-popover-spacer"))
			.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages["timeAccount.tooltip.timeAccount"])))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm").text(messages["timeAccount.tooltip.attendanceTimeDuration"]))
				.append($("<td>").addClass("font-size-sm").text(this.formatTimeAccountDuration(timeAccount.workDurationActual)))
				.append($("<td>").text(messages["timeAccount.tooltip.lastTimeAccountPeriod"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.lastWorkDurationTotalBalance))))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm").text(messages["timeAccount.tooltip.paidTimeOffDuration"]))
				.append($("<td>").addClass("font-size-sm").text(this.formatTimeAccountDuration(timeAccount.paidTimeOffDuration)))
				.append($("<td>").text(messages["timeAccount.tooltip.workDurationBalance"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.workDurationBalance))))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm").text(messages["timeAccount.tooltip.vacationDuration"]))
				.append($("<td>").addClass("font-size-sm").text(this.formatTimeAccountDuration(timeAccount.vacationDuration)))
				.append($("<td>").text(messages["timeAccount.tooltip.deductedOvertimeDuration"]))
				// DEVNOTE: deductedOvertimeDuration is positive, so we need to negate it to display it correctly
				.append($("<td>").text(this.formatTimeAccountDuration(-timeAccount.deductedOvertimeDuration))))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm").text(messages["timeAccount.tooltip.holidayDuration"]))
				.append($("<td>").addClass("font-size-sm").text(this.formatTimeAccountDuration(timeAccount.holidayDuration)))
				.append($("<td>").text(messages["timeAccount.tooltip.workDurationCorrections"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.workBalanceCorrections))))
			.append($("<tr>")
				.append($("<td>").addClass("border-top").text(messages["timeAccount.tooltip.workDurationActual"]))
				.append($("<td>").addClass("border-top").text(this.formatTimeAccountDuration(timeAccount.workDurationTotal)))
				.append($("<td>").text(messages["timeAccount.tooltip.workDurationTransfers"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.workBalanceTransfers))))
			.append($("<tr>")
				.append($("<td>").text(messages["timeAccount.tooltip.workDurationTarget"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.workDurationTarget)))
				.append($("<td>").text(messages["timeAccount.tooltip.workDurationPayoffs"]))
				.append($("<td>").text(this.formatTimeAccountDuration(timeAccount.workBalancePayoffs))))
			.append($("<tr>")
				.append($("<th>").addClass("border-top").text(messages["timeAccount.tooltip.workDurationBalance"]))
				.append($("<th>").addClass("border-top").text(this.formatTimeAccountDuration(timeAccount.workDurationBalance)))
				.append($("<th>").addClass("border-top").text(messages["timeAccount.tooltip.workDurationTotalBalance"]))
				.append($("<th>").addClass("border-top").text(this.formatTimeAccountDuration(timeAccount.workDurationTotalBalance))));
		if (SecurityStatus.isOvertimeAllowanceAllowed()) {
			if (!timeAccount.allowancesOnly) {
				table.append($("<tr>")
					.append($("<th>").text(messages["timeAccount.tooltip.overtimeBalance"]))
					.append($("<th>").text(this.formatTimeAccountDuration(timeAccount.overtimeBalance)))
					.append($("<th>").attr("colspan", 2)));
			}
			table.append($("<tr>")
				.append($("<th>").text(messages["timeAccount.tooltip.allowanceBalance"]))
				.append($("<th>").text(this.formatTimeAccountDuration(timeAccount.allowanceBalance)))
				.append($("<th>").attr("colspan", 2)));
		}
		return table;
	}

	private buildPopoverVacationAccountTable(timeAccount: TimeAccountTableRow) {
		const isExit = timeAccount.timeAccountEntryType === TimeAccountEntryType.Exit;
		const table = $("<table>").addClass("vacation-account-table");
		table.append($("<tr>")
			.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages["timeAccount.tooltip.vacationAccount"])));

		// add placeholder when both time and vacation account tables are visible so that the rows are aligned in the same way
		if (timeAccount.workingTimeTracking && !timeAccount.trustBasedWorkingTime) {
			table.append($("<tr>").append($("<td>").attr("colspan", 2)))
				.append(isExit ? "" : $("<tr>").append($("<td>").attr("colspan", 2)));
		}
		table.append($("<tr>")
			.append($("<td>").text(messages["timeAccount.tooltip.lastVacationAccountPeriod"]))
			.append($("<td>").text(this.formatTimeAccountVacation(timeAccount.lastVacationTotalBalance, timeAccount.vacationUnit))))
			.append($("<tr>")
				.append($("<td>").text(messages["timeAccount.tooltip.vacationNew"]))
				.append($("<td>").text(this.formatTimeAccountVacation(timeAccount.vacationNew, timeAccount.vacationUnit))))
			.append($("<tr>")
				.append($("<td>").text(messages["timeAccount.tooltip.vacationConsumed"]))
				.append($("<td>").text(this.formatTimeAccountVacation(timeAccount.vacationConsumed, timeAccount.vacationUnit))))
			.append($("<tr>")
				.append($("<td>").text(messages["timeAccount.tooltip.vacationCorrections"]))
				.append($("<td>").text(this.formatTimeAccountVacation(timeAccount.vacationCorrections, timeAccount.vacationUnit))));
		if (isExit) {
			table.append($("<tr>")
				.append($("<td>").text(messages["timeAccount.tooltip.vacationPayoffs"]))
				.append($("<td>").text(this.formatTimeAccountVacation(timeAccount.vacationPayoffs, timeAccount.vacationUnit))));
		}
		table.append($("<tr>")
			.append($("<th>").addClass("border-top").text(messages["timeAccount.tooltip.vacationTotalBalance"]))
			.append($("<th>").addClass("border-top").text(this.formatTimeAccountVacation(timeAccount.vacationTotalBalance, timeAccount.vacationUnit))));
		return table;
	}

	/**
	 * Creates a table with the info whether working time tracking is deactivated or trust based working time is active
	 */
	private buildPopoverWorkingTimeTrackingInfo(timeAccount: TimeAccountTableRow): JQuery {
		return $("<table>").addClass("time-account-table")
			.append($("<tr>")
				.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages.withArguments("timeAccount.tooltip.period",
					formatDate(parseISODate(timeAccount.dateFrom)),
					formatDate(parseISODate(timeAccount.date)))))
				.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages["timeAccount.tooltip.timeAccount"])))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm pull-left text-grey")
					.append($("<i>").addClass("fal fa-info-circle position-left"))
					.append($("<span>").text(!timeAccount.workingTimeTracking ?
						messages["reports.export.timeAccount.workingTimeTracking"] : messages["reports.export.timeAccount.trustBasedWorkingTime"]))));
	}

	/**
	 * Creates a table containing the info that vacation account is not active
	 */
	private buildPopoverVacationAccountInfo(): JQuery {
		return $("<table>").addClass("vacation-account-table")
			.append($("<tr>")
				.append($("<th>").attr("colspan", 2).addClass("h5 pb-10").text(messages["timeAccount.tooltip.vacationAccount"])))
			.append($("<tr>")
				.append($("<td>").addClass("font-size-sm pull-left text-grey")
					.append($("<i>").addClass("fal fa-info-circle position-left"))
					.append($("<span>").text(messages["reports.export.timeAccount.vacationAccount"]))));
	}

	private renderPopoverContent(timeAccount: TimeAccountTableRow): JQuery {
		if (!timeAccount || (timeAccount.timeAccountEntryType !== TimeAccountEntryType.Balancing && timeAccount.timeAccountEntryType !== TimeAccountEntryType.Exit)) {
			return null;
		}

		// returns popover with tables for time and vacation account side by side, an info if some features are disabled
		return $("<div>").addClass("display-flex")
			.append($("<div>")
				.append(timeAccount.workingTimeTracking && !timeAccount.trustBasedWorkingTime ? this.buildPopoverTimeAccountTable(timeAccount) : this.buildPopoverWorkingTimeTrackingInfo(timeAccount))
				.addClass("time-account-table-container"))
			.append($("<div>")
				.append(timeAccount.vacationAccount ? this.buildPopoverVacationAccountTable(timeAccount) : this.buildPopoverVacationAccountInfo())
				.addClass("vacation-account-table-container"));
	}

	private renderCreatedPopoverContent(this: void, timeAccount: TimeAccountTableRow): JQuery {
		// returns popover with tables for time and vacation account side by side, an info if some features are disabled
		return $("<div>").addClass("flex-column")
			.append($("<div>").addClass("flex-column mb-20")
				.append($("<div>").addClass("text-semibold text-muted").text(messages["timeAccount.tooltip.details"]))
			)
			.append($("<div>").addClass("flex-row")
				.append($("<div>").addClass("flex-column mr-45 text-semibold")
					.append($("<div>").text(messages["timeAccount.tooltip.createdByUser"]))
					.append($("<div>").text(messages["timeAccount.tooltip.created"]))
				)
				.append($("<div>").addClass("flex-column")
					.append($("<div>").text(timeAccount.createdByUserName))
					.append($("<div>").addClass("text-nowrap").text(formatDateTime(parseInstant(timeAccount.created)?.toZonedDateTimeISO(Temporal.Now.timeZoneId()))))
				)
			);
	}

	private formatTimeAccountDuration(value: number): string {
		return value !== null ? formatDuration(value) + " " + messages["report.timeAccountDashboard.unit.hours"] : "-";
	}

	private formatTimeAccountVacation(value: number, vacationUnit: VacationUnit): string {
		return value !== null ? formatVacation(value, vacationUnit) + " " + messages["vacationUnit." + vacationUnit] : "-";
	}
}
